// src/profile/useProfileBackfill.ts
//
// One-shot backfill for the public profile fields on /users/{uid}.
//
// The auth flow (authStore) writes `displayName` (camelCase) but not
// `display_name` or `photo_url` (snake_case) — and on profiles
// created BEFORE displayName was populated on auth.currentUser
// (e.g. early Google sign-ins), the doc may be missing both.
// usePublicProfile reads `display_name || displayName` and
// `photo_url || photoURL`, so a missing snake_case is fine if
// camelCase is set; but a missing BOTH yields the "Anonymous
// traveler" fallback even though auth has the data.
//
// This hook bridges the gap. On mount, if the user is signed in and
// auth.currentUser has a display name or photo URL that isn't
// already on the user doc, write them through (snake_case canonical
// + camelCase legacy for backwards-compatible reads).
//
// Idempotent: only writes when fields are missing/empty. Doesn't
// overwrite values the user explicitly set via ProfileEditDialog.
//
// Mount once at app shell.

import { useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { useAuthStore } from '../stores/authStore';

export function useProfileBackfill(): void {
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    let cancelled = false;

    (async () => {
      const ref = doc(db, 'users', user.uid);
      let snap;
      try {
        snap = await getDoc(ref);
      } catch (err) {
        console.warn('useProfileBackfill: read failed', err);
        return;
      }
      if (cancelled || !snap.exists()) return;
      const data = snap.data() as any;

      const fresh = auth.currentUser;
      const authName = fresh?.displayName?.trim() || '';
      const authPhoto = fresh?.photoURL || '';

      const updates: Record<string, any> = {};
      const hasName = !!(data.display_name || data.displayName);
      const hasPhoto = !!(data.photo_url || data.photoURL);

      if (!hasName && authName) {
        updates.display_name = authName;
        updates.displayName = authName;
      }
      if (!hasPhoto && authPhoto) {
        updates.photo_url = authPhoto;
        updates.photoURL = authPhoto;
      }
      if (Object.keys(updates).length === 0) return;

      try {
        await updateDoc(ref, updates);
      } catch (err) {
        console.warn('useProfileBackfill: write failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);
}
